import clsx from 'clsx';
import { RefObject, useMemo, Dispatch, SetStateAction } from 'react';
import DateInput from '@amplify/date-input';

import { DEFAULT_TIMES, CalendarWidgetPositions } from '../../constants';
import { getTimeFromDate } from '../../lib/utils';

import styles from './styles.module.scss';

type StartDateInputProps = {
  startDateInputRef: RefObject<HTMLDivElement>;
  startDate: Date | null;
  onStartDateOptionChangeHandler: () => void;
  onStartDateChangeHandler: (date: Date | null) => void;
  setIsStartDateValid: Dispatch<SetStateAction<boolean>>;
  minDate: Date | null;
  maxStartDate: Date | null;
  isStartDatePopoverOpen: boolean;
  showStartDateValidation: boolean;
  calendarPosition?: CalendarWidgetPositions;
};

export const StartDateInput = ({
  startDateInputRef,
  startDate,
  onStartDateOptionChangeHandler,
  onStartDateChangeHandler,
  setIsStartDateValid,
  minDate,
  maxStartDate,
  isStartDatePopoverOpen,
  showStartDateValidation,
  calendarPosition = CalendarWidgetPositions.LEFT,
}: StartDateInputProps) => {
  const defaultDate = useMemo(() => {
    return startDate ?? null;
  }, [startDate]);

  const defaultTime = useMemo(() => {
    return defaultDate
      ? getTimeFromDate(defaultDate.valueOf())
      : getTimeFromDate(startDate?.valueOf());
  }, [defaultDate, startDate]);

  return (
    <div
      ref={startDateInputRef}
      className={clsx(styles.startDateInputWrapper, 'start-date-input-wrapper')}
      aria-label="Choose a start date"
      onClick={() => onStartDateOptionChangeHandler()}
      onKeyDown={() => onStartDateOptionChangeHandler()}
      role="textbox"
      tabIndex={0}
      data-testid="start-date-input-wrapper"
    >
      <DateInput
        defaultDate={defaultDate}
        defaultTime={defaultTime ?? DEFAULT_TIMES.startDateTime}
        onDateChange={onStartDateChangeHandler}
        onValidation={setIsStartDateValid}
        minDate={minDate}
        maxDate={maxStartDate}
        showCalendar={isStartDatePopoverOpen}
        showValidation={showStartDateValidation}
        dateInputRef={startDateInputRef}
        calendarPosition={calendarPosition}
      />
    </div>
  );
};
